import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from '../components/icons';
import { cx, FOCUS_RING } from '../components/styles';
import { CONTAINER, SECTION_PADDING, TRIAL_DAYS } from './landingConfig';
import { Reveal } from './Reveal';

/**
 * Section 9 — FAQ (DESIGN_PLAN §1.9). Native `<details>`/`<summary>`, so
 * keyboard and screen-reader behaviour is correct for free.
 *
 * FOUR ROWS SHIP, NOT SIX. The plan's rule is explicit: "if any ⚠️ answer
 * cannot be confirmed, delete that FAQ row. Do not soften it into something
 * vague. A missing FAQ is free; a wrong one is not." Two of the five rows are
 * blocked on open questions 5 and 6 and neither is answered, so they are held
 * below as comments with their exact copy, ready to re-add verbatim once
 * confirmed:
 *
 *   Q: `Is my card safe?`
 *   A: `Card details go straight to Stripe. They never reach our servers.`
 *      Needs: confirmation this is true of EVERY payment path, not just the
 *      DICE add-card flow.
 *
 *   Q: `Can I cancel?`
 *   A: `Yes. Cancel any time and you keep access until the end of the period
 *      you paid for.`
 *      Needs: confirmation for FlashTools plans (verified only for
 *      Dice-number subscriptions).
 */
interface FaqItem {
  q: string;
  a: React.ReactNode;
}

const FAQS: FaqItem[] = [
  {
    // The most-asked support question, and the reason prices went on this page:
    // people finished the trial without knowing whether they had to go pay
    // somewhere. They do not — Checkout takes the card up front and Stripe
    // charges it at trial end. Verified against `create_checkout_session` in
    // oneClickTixCore `flashtools/services/billing.py`.
    q: 'What happens when the free trial ends?',
    a: `Nothing to do — you are already signed up. Checkout takes your card when you start, and after ${TRIAL_DAYS} days Core bills $30/month automatically. There is no second signup. To change plans or cancel, open Billing in the app.`,
  },
  {
    q: 'Which platforms work with FlashTools?',
    a: 'DICE, AXS and TAO for your tickets. CrowdVolt for your listings.',
  },
  {
    q: 'Do I need a Chrome extension?',
    a: (
      <>
        Only for CrowdVolt. It takes about two minutes to set up.{' '}
        <Link
          to="/help"
          className={cx('rounded text-[#60a5fa] underline-offset-2 hover:underline', FOCUS_RING)}
        >
          Read the setup guide
        </Link>
      </>
    ),
  },
  {
    q: 'What is a Dice account number?',
    a: 'A phone number we rent for you so you can run a second DICE account. Texts to it forward to your email.',
  },
];

export const FaqAccordion: React.FC = () => (
  <Reveal as="section" className={SECTION_PADDING}>
    <div className={cx(CONTAINER, 'max-w-[820px]')}>
      <h2 className="text-3xl font-bold tracking-tight text-white">Questions.</h2>
      <div className="mt-8 divide-y divide-white/[0.06] border-y border-white/[0.06]">
        {FAQS.map((item) => (
          <details key={item.q} className="group">
            <summary
              className={cx(
                'flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-left marker:content-none',
                FOCUS_RING,
              )}
            >
              <span className="text-base font-medium text-white">{item.q}</span>
              <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-white/40 transition-transform group-open:rotate-180" />
            </summary>
            <p className="pb-5 pr-8 text-sm leading-relaxed text-white/60">{item.a}</p>
          </details>
        ))}
      </div>
    </div>
  </Reveal>
);
